import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import {
  DispersionListQuery,
  DispersionRepository,
} from '@app/domain/ports/tpv/dispersion.repository';
import { TpvDispersion } from '@app/domain/models/tpv/dispersion.model';
import { TpvPage } from '@app/domain/models/tpv/tpv-page.model';
import { DispersionDto } from '@app/data/dtos/tpv/dispersion.dto';
import { mapDispersion } from '@app/data/mappers/tpv/dispersion.mapper';
import { toTpvPage } from '@app/data/mappers/tpv/tpv-page.mapper';
import { tpvUrl } from '@app/data/tpv/tpv-api.config';
import { TpvEnvelope, unwrapTpv } from '@app/data/tpv/tpv-envelope';
import { toHttpParams } from './tpv-http.util';

/** `GET /api/v1/tpv/dispersions/`. Auth vía interceptores estándar. */
@Injectable()
export class DispersionRemoteDataSource extends DispersionRepository {
  private readonly http = inject(HttpClient);

  override getDispersions(
    externalIdMerchant?: string,
    query: DispersionListQuery = {},
  ): Observable<TpvPage<TpvDispersion>> {
    const page = query.page ?? 1;
    const limit = query.limit ?? 10;
    // Sin `id_merchant` → el backend acota a los comercios del usuario (toHttpParams
    // omite el valor undefined, así que el param simplemente no se envía).
    const params = toHttpParams({
      id_merchant: externalIdMerchant,
      page,
      limit,
      start_date: query.startDate,
      end_date: query.endDate,
      // Caja libre: el componente manda el mismo término a los tres params y
      // el micro hace OR server-side contra nombre de comercio / empresa /
      // unidad (contrato suite #278 + micro #42, 2026-10-05). Antes, en
      // dispersiones, `merchant_name` solo encontraba el nombre completo
      // exacto y case-sensitive.
      merchant_name: query.merchantName,
      company_name: query.companyName,
      unit_name: query.unitName,
      // `min_amount` y `max_amount` son INDEPENDIENTES (mismo contrato): mandar
      // solo uno ya NO fuerza 502 como antes.
      min_amount: query.minAmount,
      max_amount: query.maxAmount,
      status: query.status,
      processor: query.processor,
    });

    return this.http
      .get<TpvEnvelope<unknown>>(tpvUrl('dispersions/'), { params })
      .pipe(map((raw) => toTpvPage<DispersionDto, TpvDispersion>(raw, mapDispersion, { page, limit })));
  }

  override getDispersionById(
    idDispersion: string | number,
  ): Observable<TpvDispersion | null> {
    return this.http
      .get<TpvEnvelope<DispersionDto | DispersionDto[]>>(tpvUrl(`dispersions/${idDispersion}/`))
      .pipe(
        map((raw) => {
          const data = unwrapTpv<DispersionDto | DispersionDto[]>(raw);
          const dto = Array.isArray(data) ? data[0] : data;
          return dto ? mapDispersion(dto) : null;
        }),
      );
  }
}
